import { atom } from 'jotai'
import {
  AiArtworkOptionTypeEnum,
  AiCreateArtworkModel,
  AiCreateArtworkPattern,
  MJQuality,
  MemberCardRightTypeEnum,
  PretreatmentMethod,
  SuperType,
  getAIMemberList_accountModule_userMemberCardList,
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu,
  getAITotalInfo_pictureGraph_aiArtworkTotalInfo,
  ControlNetPulidMode,
  SpellAnalysisImgType,
  InitImageSource,
} from '~/graphqls/types'

export const DEFAULT_OPTIONS = {
  MODEL_CODE: 0,
  BASE_POINT: 2, // 默认1次作画扣2个基础积分
  SIZE: '1:1',
  CREATIVITY_DEGREE: 50,
  /**  透明图：权重  */
  TRANSPARENT_WEIGHT: 1,
  /** 透明图：Stop At */
  ENDING_STEP: 1,
  /** 参考强度 */
  WEIGHT: 1.0,
  /** 角色模式 */
  PULID_MODE: ControlNetPulidMode.FIDELITY,
  /** 角色参考强度 */
  ROLE_WEIGHT: 0.6,
  /** mj参考强度 */
  MJ_WEIGHT: 100,
}

export interface StyleDecorationInput {
  key: string
  choiceKey?: number
  type: AiArtworkOptionTypeEnum
}

/** 作画信息 */
export const totalInfoAtom =
  atom<getAITotalInfo_pictureGraph_aiArtworkTotalInfo | null>(null)

/** 权益卡列表 */
export const memberListAtom = atom<
  getAIMemberList_accountModule_userMemberCardList[]
>([])

/** 权益卡可用作画积分 */
export const createPointsAtom = atom(get => {
  let createPoints = 0
  const memberList = get(memberListAtom)

  memberList.forEach(item => {
    item.holdCardInfo?.forEach(i => {
      if (i?.rightType === MemberCardRightTypeEnum.TEMPORARY_INTEGRAL) {
        createPoints += i.num || 0
      }
    })
  })

  return createPoints
})

/** 可用作画积分 */
export const totalPaintPointsAtom = atom(get => {
  const totalInfo = get(totalInfoAtom)
  const createPoints = get(createPointsAtom)

  return totalInfo?.userIntegral! + createPoints
})

/** 单个大尺寸消耗的积分 */
export const resolutionPointAtom = atom(0)
/** 大尺寸原始价格 */
export const resolutionOriginalPointAtom = atom(0)

/** 单个精绘消耗的积分 */
export const prefinePointAtom = atom(0)
/** 精绘原始价格 */
export const prefineOriginalPointAtom = atom(0)

/** AI作画选项 */
export const optionsAtom =
  atom<getAIMenu_pictureGraph_aiCreateArtworkOptionMenu | null>(null)

/** 选择的大模型 */
export const selectModelAtom = atom<AiCreateArtworkModel>(
  AiCreateArtworkModel.STABLE_DIFFUSION
)

/** modelCode */
export const modelCodeAtom = atom(DEFAULT_OPTIONS.MODEL_CODE)

/** 模式 */
export const patternAtom = atom<AiCreateArtworkPattern>(
  AiCreateArtworkPattern.GENERAL
)

/** 画面描述 */
export const promptAtom = atom('')

/** 反向描述 */
export const ucPromptAtom = atom<string | undefined>(undefined)

// 风格模型
export const styleModelAtom = atom<string | null>(null)

/** tag大全分类id */
export const tagCategoryIdAtom = atom<number | null | undefined>(undefined)

/** 创意度 */
export const creativityDegreeAtom = atom<number>(
  DEFAULT_OPTIONS.CREATIVITY_DEGREE
)

/** 融合模型 */
export const modelFusionAtom = atom<{ key: string; weight: number }[]>([])

/** 作画次数 */
export const paintTimesAtom = atom<number>(1)

/** 作画次数步长 */
export const paintTimesStepAtom = atom<number>(1)

/** 涂抹组件的ref */
export const paintRefAtom = atom<{
  saveMask?: () => Promise<[string, string] | undefined>
  reset?: () => void
}>({})

/** 涂抹图片URL */
export const maskImageUrlAtom = atom('')

/** 涂抹底图来源 */
export const initImageSourcesAtom = atom<InitImageSource[] | undefined>(
  undefined
)

/** 底图URL */
export const initImageUrlAtom = atom<string | undefined>(undefined)

/** 尺寸key */
export const resolutionKeyAtom = atom<string | undefined>(undefined)

/** 尺寸 */
export const selectSizeAtom = atom(DEFAULT_OPTIONS.SIZE)

/** resolution */
export const selectResolutionAtom = atom('')

/** 标签选择 */
export const styleDecorationAtom = atom<StyleDecorationInput[]>([])

/** 高级设置 */
export const hasAdvanceAtom = atom(false)

/** 修手 */
export const doRepairTheHandAtom = atom(false)

/** 随机种子 */
export const seedAtom = atom<string | undefined>(undefined)

/** 采样模式 */
export const selectSamplerAtom = atom<number[] | undefined>([])

/** 角色同人 */
export const characterAtom = atom<string[]>([])

/** 提示词相关性 */
export const cfgAtom = atom<number | undefined>(undefined)

/** ensd */
export const ensdAtom = atom<number | undefined>(undefined)

/** clipSkip */
export const clipSkipAtom = atom<number | undefined>(undefined)

/** 图片需要预处理 */
export const pretreatmentMethodAtom = atom<PretreatmentMethod | undefined>(
  undefined
)

/** 精绘 */
export const controlPrefineAtom = atom<SuperType>(SuperType.SUPERSIZE)

/** 百度统计事件 */
export const trackEventLabelAtom = atom('')

/** 潮汐模式生成 */
export const genTidalAtom = atom(false)

/** 风格差异化 */
export const styleDiffAtom = atom(0)

/** 风格艺术化 */
export const styleArtisticAtom = atom(100)

/** MidJourney / NikkiJourney 参数quality: 最大细节精度 */
export const mjQualityAtom = atom(MJQuality.QUALITY_100)

/** MidJourney / NikkiJourney 参数upbeta: 是否增加细节 */
export const mjUpbetaAtom = atom(false)

/** MidJourney / NikkiJourney 参数tile: 是否生成无缝贴图 */
export const mjTileAtom = atom(false)

/** 风格模型-MidJourney */
export const isMJStyleModelAtom = atom<boolean>(false)

/** 透明图：权重 */
export const transparentWeightAtom = atom(DEFAULT_OPTIONS.TRANSPARENT_WEIGHT)

/** 透明图：Stop At */
export const endingStepAtom = atom(DEFAULT_OPTIONS.ENDING_STEP)

/** 风格参考 */
export const referToStyleAtom = atom<{
  url?: string
  weight: number
  mjWeight: number
}>({
  weight: DEFAULT_OPTIONS.WEIGHT,
  mjWeight: DEFAULT_OPTIONS.MJ_WEIGHT,
})
/** 角色参考 */
export const referToRoleAtom = atom<{
  url?: string
  weight: number
  pulidMode: ControlNetPulidMode
  mjWeight: number
}>({
  weight: DEFAULT_OPTIONS.ROLE_WEIGHT,
  pulidMode: DEFAULT_OPTIONS.PULID_MODE,
  mjWeight: DEFAULT_OPTIONS.MJ_WEIGHT,
})
/** 结构参考 */
export const referToStructureAtom = atom<{
  url?: string
  weight: number
}>({
  weight: DEFAULT_OPTIONS.WEIGHT,
})

/** 咒语解析 */
export const spellAnalysisImgTypeAtom = atom<SpellAnalysisImgType | undefined>(
  undefined
)

/**
 * 4k
 */
/** 步长比例 */
export const cutDiffProportionAtom = atom<number | undefined>(undefined)
/** 提示词相关性-1 */
export const cfgScale1Atom = atom<number | undefined>(undefined)
/** 提示词相关性-1 */
export const cfgScale2Atom = atom<number | undefined>(undefined)

/**
 * flux-ifg
 */
/** ifg 混沌参数 */
export const choasAtom = atom<number | undefined>(undefined)
/** ifg 风格参数, 后端叫style实际应该叫stylize 这里和后端保持统一 */
export const styleAtom = atom<number | undefined>(undefined)
